import { useState, useRef, useEffect } from 'react';
import { templates } from '../templates/templateRegistry';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import './LiveRecorder.css';

function LiveRecorder() {
  const [micDevices, setMicDevices] = useState([]);
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('logo_waveform_2d');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [micPermission, setMicPermission] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const animationFrameRef = useRef(null);
  const previewIntervalRef = useRef(null);
  const ffmpegRef = useRef(new FFmpeg());
  const ffmpegLoadedRef = useRef(false);

  // Load microphone devices and request permission
  useEffect(() => {
    async function loadMicrophones() {
      try {
        // Request microphone permission first
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicPermission(true);
        
        // Get all audio devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const mics = devices.filter(device => device.kind === 'audioinput');
        console.log('Found microphones:', mics);
        setMicDevices(mics);
        
        if (mics.length > 0) {
          setSelectedMic(mics[0].deviceId);
        }
        
        // Stop the permission stream
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        console.error('Error loading microphones:', error);
        alert('Microphone permission is required. Please allow microphone access and reload the page.');
      }
    }
    loadMicrophones();
    
    // Load FFmpeg
    loadFFmpeg();
  }, []);

  const loadFFmpeg = async () => {
    if (ffmpegLoadedRef.current) return;
    
    try {
      const ffmpeg = ffmpegRef.current;
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.4/dist/umd';
      
      ffmpeg.on('log', ({ message }) => {
        console.log('FFmpeg:', message);
      });
      
      ffmpeg.on('progress', ({ progress }) => {
        setConversionProgress(Math.round(progress * 100));
      });
      
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      
      ffmpegLoadedRef.current = true;
      console.log('FFmpeg loaded successfully');
    } catch (error) {
      console.error('Failed to load FFmpeg:', error);
    }
  };

  // Start preview animation when logo or template changes
  useEffect(() => {
    if (!isRecording) {
      startPreview();
    }
    return () => {
      if (previewIntervalRef.current) {
        clearInterval(previewIntervalRef.current);
      }
    };
  }, [logoImage, selectedTemplate]);

  const startPreview = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const template = templates.find(t => t.id === selectedTemplate);
    
    // Clear any existing preview interval
    if (previewIntervalRef.current) {
      clearInterval(previewIntervalRef.current);
    }
    
    // Generate static preview
    const renderStaticPreview = () => {
      if (isRecording) return; // Don't render static preview during recording
      
      // Create fake audio data for preview (sine wave pattern)
      const dataArray = new Uint8Array(1024);
      const time = Date.now() / 1000;
      for (let i = 0; i < dataArray.length; i++) {
        dataArray[i] = Math.abs(Math.sin(i * 0.05 + time) * 128 + 64);
      }
      
      if (template) {
        renderTemplate(ctx, canvas.width, canvas.height, dataArray, template);
      }
    };
    
    // Render preview immediately
    renderStaticPreview();
    
    // Update preview every 50ms for smooth animation
    previewIntervalRef.current = setInterval(renderStaticPreview, 50);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setLogoUrl(url);
      
      // Load image
      const img = new Image();
      img.onload = () => {
        console.log('Logo loaded:', img.width, 'x', img.height);
        setLogoImage(img);
      };
      img.onerror = () => {
        console.error('Failed to load logo image');
        alert('Failed to load logo. Please try another image.');
      };
      img.src = url;
    }
  };

  const startRecording = async () => {
    try {
      console.log('Starting recording with mic:', selectedMic);
      
      // Stop preview animation
      if (previewIntervalRef.current) {
        clearInterval(previewIntervalRef.current);
      }
      
      // Get audio stream with selected microphone and high quality settings
      const constraints = {
        audio: {
          deviceId: selectedMic ? { exact: selectedMic } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000, // High quality sample rate
          channelCount: 2 // Stereo
        }
      };
      
      const audioStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = audioStream;
      console.log('Audio stream obtained with high quality settings');

      // Setup audio context for visualization
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      
      const source = audioContextRef.current.createMediaStreamSource(audioStream);
      source.connect(analyserRef.current);
      console.log('Audio analyzer connected');

      // Setup audio recording with higher quality
      const audioOptions = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000 // 128 kbps for high quality
      };
      
      // Fallback if opus not supported
      if (!MediaRecorder.isTypeSupported(audioOptions.mimeType)) {
        audioOptions.mimeType = 'audio/webm';
      }
      
      mediaRecorderRef.current = new MediaRecorder(audioStream, audioOptions);
      console.log('Audio recording codec:', audioOptions.mimeType);
      
      const audioChunks = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      // Setup canvas recording
      const canvas = canvasRef.current;
      const canvasStream = canvas.captureStream(30); // 30 FPS
      
      // Try different codecs
      let mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
      }
      console.log('Using video codec:', mimeType);
      
      videoRecorderRef.current = new MediaRecorder(canvasStream, {
        mimeType: mimeType,
        videoBitsPerSecond: 5000000 // Increased to 5 Mbps for better quality
      });

      const videoChunks = [];
      videoRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunks.push(event.data);
        }
      };

      videoRecorderRef.current.onstop = async () => {
        console.log('Video recording stopped, creating blob...');
        const videoBlob = new Blob(videoChunks, { type: 'video/webm' });
        console.log('Video blob size:', videoBlob.size);
        
        // Convert to MP4
        await convertToMP4(videoBlob);
      };

      // Start recording
      mediaRecorderRef.current.start(100); // Collect data every 100ms
      videoRecorderRef.current.start(100);
      setIsRecording(true);
      console.log('Recording started');

      // Start animation loop
      animate();
    } catch (error) {
      console.error('Error starting recording:', error);
      alert(`Failed to start recording: ${error.message}\n\nPlease check:\n1. Microphone permissions are granted\n2. Selected microphone is available\n3. No other app is using the microphone`);
    }
  };

  const stopRecording = () => {
    console.log('Stopping recording...');
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (videoRecorderRef.current && videoRecorderRef.current.state !== 'inactive') {
      videoRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setIsRecording(false);
    
    // Restart preview
    setTimeout(() => startPreview(), 100);
  };

  const animate = () => {
    if (!analyserRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!isRecording && (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive')) return;

      animationFrameRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      // Get selected template
      const template = templates.find(t => t.id === selectedTemplate);
      if (template) {
        renderTemplate(ctx, canvas.width, canvas.height, dataArray, template);
      }
    };

    draw();
  };

  const renderTemplate = (ctx, width, height, dataArray, template) => {
    const config = template.defaultConfig;
    const centerX = width / 2;
    const centerY = height / 2;

    // Clear canvas
    ctx.fillStyle = config.backgroundColor;
    ctx.fillRect(0, 0, width, height);

    if (template.id === 'logo_waveform_2d') {
      // Draw waveform
      const centerY = height / 2;
      const waveformHeight = config.waveformHeight;
      
      ctx.beginPath();
      ctx.lineWidth = 3;
      const gradient = ctx.createLinearGradient(0, centerY - waveformHeight, 0, centerY + waveformHeight);
      gradient.addColorStop(0, config.primaryColor);
      gradient.addColorStop(0.5, config.secondaryColor);
      gradient.addColorStop(1, config.primaryColor);
      ctx.strokeStyle = gradient;

      const sliceWidth = width / dataArray.length;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] / 255.0) * config.sensitivity;
        const y = centerY + (v - 0.5) * waveformHeight * 2;
        
        if (i === 0) {
          ctx.moveTo(0, y);
        } else {
          ctx.lineTo(i * sliceWidth, y);
        }
      }
      ctx.stroke();

      // Draw logo
      if (logoImage) {
        const logoSize = 150 * config.logoScale;
        ctx.shadowColor = config.primaryColor;
        ctx.shadowBlur = 30;
        ctx.drawImage(logoImage, (width - logoSize) / 2, height / 4 - logoSize / 2, logoSize, logoSize);
        ctx.shadowBlur = 0;
      }
    } else if (template.id === 'circular_spectrum') {
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = config.radius;
      const bars = config.barCount;

      // Draw spectrum bars
      for (let i = 0; i < bars; i++) {
        const angle = (i / bars) * Math.PI * 2;
        const dataIndex = Math.floor((i / bars) * dataArray.length);
        const value = (dataArray[dataIndex] / 255.0) * config.sensitivity;
        const barHeight = value * radius * 0.8;

        const x1 = centerX + Math.cos(angle) * radius;
        const y1 = centerY + Math.sin(angle) * radius;
        const x2 = centerX + Math.cos(angle) * (radius + barHeight);
        const y2 = centerY + Math.sin(angle) * (radius + barHeight);

        ctx.beginPath();
        ctx.strokeStyle = `hsl(${(i / bars) * 360}, 70%, 60%)`;
        ctx.lineWidth = 3;
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      // Draw circular logo with clipping
      if (logoImage) {
        const logoSize = radius * 1.8 * config.logoScale;
        const logoX = centerX - logoSize / 2;
        const logoY = centerY - logoSize / 2;
        
        // Create circular clipping path
        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, logoSize / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        
        // Draw logo inside circle
        ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
        
        // Add circular border
        ctx.restore();
        ctx.beginPath();
        ctx.arc(centerX, centerY, logoSize / 2, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 4;
        ctx.stroke();
      }
    } else if (template.id === 'horizontal_bars') {
      const bars = config.barCount;
      const barWidth = config.barWidth;
      const barGap = config.barGap;
      const totalBarWidth = barWidth + barGap;
      const startX = (width - (bars * totalBarWidth)) / 2;

      for (let i = 0; i < bars; i++) {
        const dataIndex = Math.floor((i / bars) * dataArray.length);
        const value = (dataArray[dataIndex] / 255.0) * config.sensitivity;
        const barHeight = value * height * 0.8;
        const x = startX + i * totalBarWidth;
        const y = height - barHeight;

        const gradient = ctx.createLinearGradient(x, y, x, height);
        gradient.addColorStop(0, config.primaryColor);
        gradient.addColorStop(1, config.secondaryColor);
        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, barWidth, barHeight);
      }

      if (logoImage) {
        const logoSize = Math.min(width, height) * 0.15 * config.logoScale;
        ctx.drawImage(logoImage, width - logoSize - 40, 40, logoSize, logoSize);
      }
    } else if (template.id === 'neon_pulse') {
      const centerX = width / 2;
      const centerY = height / 2;
      const time = Date.now() / 1000;
      
      // Calculate average amplitude
      const avgAmplitude = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length / 255;
      
      // Draw pulsating neon rings
      for (let i = 0; i < config.ringCount; i++) {
        const ringProgress = (i / config.ringCount);
        const pulse = Math.sin(time * 2 + ringProgress * Math.PI * 2) * 0.5 + 0.5;
        const ringSize = (config.maxRingSize * ringProgress) + (avgAmplitude * config.sensitivity * 100);
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, ringSize, 0, Math.PI * 2);
        
        const gradient = ctx.createRadialGradient(centerX, centerY, ringSize - 20, centerX, centerY, ringSize);
        gradient.addColorStop(0, `${config.primaryColor}00`);
        gradient.addColorStop(0.7, `${config.secondaryColor}${Math.floor(pulse * 255).toString(16).padStart(2, '0')}`);
        gradient.addColorStop(1, `${config.accentColor}00`);
        
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 15;
        ctx.shadowBlur = 30;
        ctx.shadowColor = config.primaryColor;
        ctx.stroke();
      }
      
      ctx.shadowBlur = 0;
      
      if (logoImage) {
        const logoSize = 180 * config.logoScale;
        ctx.drawImage(logoImage, centerX - logoSize / 2, centerY - logoSize / 2, logoSize, logoSize);
      }
    } else if (template.id === 'stereo_waves') {
      const centerY = height / 2;
      const waveformHeight = config.waveformHeight;
      
      // Top waveform
      ctx.beginPath();
      ctx.lineWidth = config.lineWidth;
      ctx.strokeStyle = config.primaryColor;
      const sliceWidth = width / dataArray.length;
      
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] / 255.0) * config.sensitivity;
        const y = centerY - v * waveformHeight;
        if (i === 0) ctx.moveTo(0, y);
        else ctx.lineTo(i * sliceWidth, y);
      }
      ctx.stroke();
      
      // Bottom waveform (mirrored)
      ctx.beginPath();
      ctx.strokeStyle = config.secondaryColor;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] / 255.0) * config.sensitivity;
        const y = centerY + v * waveformHeight;
        if (i === 0) ctx.moveTo(0, y);
        else ctx.lineTo(i * sliceWidth, y);
      }
      ctx.stroke();
      
      if (logoImage) {
        const logoSize = 200 * config.logoScale;
        ctx.shadowColor = config.primaryColor;
        ctx.shadowBlur = 40;
        ctx.drawImage(logoImage, centerX - logoSize / 2, centerY - logoSize / 2, logoSize, logoSize);
        ctx.shadowBlur = 0;
      }
    } else if (template.id === 'gradient_bars') {
      const bars = config.barCount;
      const barWidth = config.barWidth;
      const barGap = config.barGap;
      const totalBarWidth = barWidth + barGap;
      const startX = (width - (bars * totalBarWidth)) / 2;
      
      for (let i = 0; i < bars; i++) {
        const dataIndex = Math.floor((i / bars) * dataArray.length);
        const value = (dataArray[dataIndex] / 255.0) * config.sensitivity;
        const barHeight = value * height * 0.8;
        const x = startX + i * totalBarWidth;
        const y = height - barHeight;
        
        if (config.useRainbow) {
          const hue = (i / bars) * 360;
          ctx.fillStyle = `hsl(${hue}, 85%, 60%)`;
        } else {
          const gradient = ctx.createLinearGradient(x, y, x, height);
          gradient.addColorStop(0, config.primaryColor);
          gradient.addColorStop(1, config.secondaryColor);
          ctx.fillStyle = gradient;
        }
        
        ctx.fillRect(x, y, barWidth, barHeight);
      }
      
      if (logoImage) {
        const logoSize = 180 * config.logoScale;
        ctx.drawImage(logoImage, centerX - logoSize / 2, 60, logoSize, logoSize);
      }
    } else if (template.id === 'modern_minimal') {
      const centerX = width / 2;
      const bars = config.barCount;
      const barWidth = 20;
      const barGap = 8;
      const totalBarWidth = barWidth + barGap;
      const startX = centerX - (bars * totalBarWidth) / 2;
      
      for (let i = 0; i < bars; i++) {
        const dataIndex = Math.floor((i / bars) * dataArray.length);
        const value = (dataArray[dataIndex] / 255.0) * config.sensitivity;
        const barHeight = value * height * 0.5;
        const x = startX + i * totalBarWidth;
        const y = height - barHeight - 100;
        
        ctx.fillStyle = config.primaryColor;
        if (config.showShadow) {
          ctx.shadowColor = config.accentColor;
          ctx.shadowBlur = 10;
          ctx.shadowOffsetY = 5;
        }
        ctx.fillRect(x, y, barWidth, barHeight);
      }
      
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      
      if (logoImage) {
        const logoSize = 250 * config.logoScale;
        ctx.drawImage(logoImage, centerX - logoSize / 2, 80, logoSize, logoSize);
      }
    } else if (template.id === 'retro_synthwave') {
      const centerX = width / 2;
      const centerY = height / 2;
      
      // Draw perspective grid
      if (config.showGrid) {
        ctx.strokeStyle = config.gridColor + '40';
        ctx.lineWidth = 2;
        
        // Horizontal lines
        for (let i = 0; i < 20; i++) {
          const y = height - (i * 40);
          const perspective = 1 - (i / 20);
          const lineWidth = width * perspective;
          ctx.beginPath();
          ctx.moveTo(centerX - lineWidth / 2, y);
          ctx.lineTo(centerX + lineWidth / 2, y);
          ctx.stroke();
        }
        
        // Vertical lines
        for (let i = -5; i <= 5; i++) {
          ctx.beginPath();
          const xStart = centerX + (i * 100);
          ctx.moveTo(xStart, height);
          ctx.lineTo(centerX + (i * 200), 0);
          ctx.stroke();
        }
      }
      
      // Draw audio bars on horizon
      const bars = 32;
      const barWidth = 20;
      const barGap = 10;
      const totalBarWidth = barWidth + barGap;
      const startX = centerX - (bars * totalBarWidth) / 2;
      const horizonY = height * 0.6;
      
      for (let i = 0; i < bars; i++) {
        const dataIndex = Math.floor((i / bars) * dataArray.length);
        const value = (dataArray[dataIndex] / 255.0) * config.sensitivity;
        const barHeight = value * 300;
        const x = startX + i * totalBarWidth;
        const y = horizonY - barHeight;
        
        const gradient = ctx.createLinearGradient(x, y, x, horizonY);
        gradient.addColorStop(0, config.primaryColor);
        gradient.addColorStop(0.5, config.secondaryColor);
        gradient.addColorStop(1, config.accentColor);
        
        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, barWidth, barHeight);
      }
      
      if (logoImage) {
        const logoSize = 200 * config.logoScale;
        ctx.shadowColor = config.primaryColor;
        ctx.shadowBlur = 50;
        ctx.drawImage(logoImage, centerX - logoSize / 2, 60, logoSize, logoSize);
        ctx.shadowBlur = 0;
      }
    } else if (template.id === 'particle_burst') {
      const centerX = width / 2;
      const centerY = height / 2;
      const time = Date.now() / 1000;
      
      // Draw particles
      for (let i = 0; i < config.particleCount; i++) {
        const angle = (i / config.particleCount) * Math.PI * 2;
        const dataIndex = Math.floor((i / config.particleCount) * dataArray.length);
        const value = (dataArray[dataIndex] / 255.0) * config.sensitivity;
        const distance = 100 + value * 300;
        
        const x = centerX + Math.cos(angle + time) * distance;
        const y = centerY + Math.sin(angle + time) * distance;
        
        ctx.beginPath();
        ctx.arc(x, y, config.particleSize + value * 5, 0, Math.PI * 2);
        
        const particleColor = value > 0.5 ? config.primaryColor : config.secondaryColor;
        ctx.fillStyle = particleColor;
        ctx.shadowColor = particleColor;
        ctx.shadowBlur = 15;
        ctx.fill();
      }
      
      ctx.shadowBlur = 0;
      
      if (logoImage) {
        const logoSize = 180 * config.logoScale;
        ctx.drawImage(logoImage, centerX - logoSize / 2, centerY - logoSize / 2, logoSize, logoSize);
      }
    } else if (template.id === 'waveform_sphere') {
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = config.sphereRadius;
      const segments = config.segments;
      
      // Draw sphere wireframe with audio data
      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const dataIndex = Math.floor((i / segments) * dataArray.length);
        const value = (dataArray[dataIndex] / 255.0) * config.sensitivity;
        const sphereRadius = radius + value * 80;
        
        // Latitude lines
        for (let j = 0; j < 8; j++) {
          const lat = (j / 8) * Math.PI - Math.PI / 2;
          const latRadius = Math.cos(lat) * sphereRadius;
          const y = centerY + Math.sin(lat) * sphereRadius;
          
          const x1 = centerX + Math.cos(angle) * latRadius;
          const x2 = centerX + Math.cos(angle + Math.PI * 2 / segments) * latRadius;
          
          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          
          const brightness = 50 + value * 50;
          ctx.strokeStyle = `hsl(${200 + value * 60}, 70%, ${brightness}%)`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
      
      if (logoImage) {
        const logoSize = radius * 1.2 * config.logoScale;
        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, logoSize / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(logoImage, centerX - logoSize / 2, centerY - logoSize / 2, logoSize, logoSize);
    }
  };

  const convertToMP4 = async (webmBlob) => {
    try {
      if (!ffmpegLoadedRef.current) {
        console.log('FFmpeg not loaded, downloading WebM instead...');
        downloadVideo(webmBlob, 'webm');
        return;
      }

      setIsConverting(true);
      setConversionProgress(0);
      console.log('Starting conversion to MP4...');

      const ffmpeg = ffmpegRef.current;
      
      // Write input file
      await ffmpeg.writeFile('input.webm', await fetchFile(webmBlob));
      
      // Convert to MP4
      await ffmpeg.exec([
        '-i', 'input.webm',
        '-c:v', 'libx264',
        '-preset', 'fast',
        '-crf', '22',
        '-c:a', 'aac',
        '-b:a', '192k',
        'output.mp4'
      ]);
      
      // Read output file
      const data = await ffmpeg.readFile('output.mp4');
      const mp4Blob = new Blob([data.buffer], { type: 'video/mp4' });
      
      console.log('Conversion complete! MP4 size:', mp4Blob.size);
      
      // Clean up
      await ffmpeg.deleteFile('input.webm');
      await ffmpeg.deleteFile('output.mp4');
      
      setIsConverting(false);
      downloadVideo(mp4Blob, 'mp4');
    } catch (error) {
      console.error('Error converting to MP4:', error);
      setIsConverting(false);
      alert('Failed to convert to MP4. Downloading as WebM instead.');
      downloadVideo(webmBlob, 'webm');
    }
  };

  const downloadVideo = (videoBlob, format = 'mp4') => {
    const url = URL.createObjectURL(videoBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `voice-recording-${Date.now()}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="live-recorder">
      <div className="controls-section">
        <div className="control-group">
          <label htmlFor="mic-select">🎤 Microphone</label>
          <select
            id="mic-select"
            value={selectedMic}
            onChange={(e) => setSelectedMic(e.target.value)}
            disabled={isRecording}
          >
            {micDevices.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="template-select">🎨 Template</label>
          <select
            id="template-select"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            disabled={isRecording}
          >
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="logo-upload">🖼️ Logo (optional)</label>
          <input
            id="logo-upload"
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            disabled={isRecording}
          />
          {logoUrl && <span className="logo-indicator">✓ Logo uploaded</span>}
        </div>
      </div>

      <div className="preview-section">
        <h3>Live Preview</h3>
        <canvas
          ref={canvasRef}
          width={1920}
          height={1080}
          className="preview-canvas"
        />
      </div>

      <div className="recording-controls">
        {!isRecording ? (
          <button
            onClick={startRecording}
            className="btn-record"
            disabled={!selectedMic || !micPermission}
          >
            ⏺️ Start Recording
          </button>
        ) : (
          <button onClick={stopRecording} className="btn-stop">
            ⏹️ Stop Recording
          </button>
        )}
      </div>

      {isRecording && (
        <div className="recording-indicator">
          <span className="recording-dot"></span>
          Recording in progress...
        </div>
      )}

      {isConverting && (
        <div className="conversion-indicator">
          <div className="conversion-progress">
            <div className="progress-bar" style={{ width: `${conversionProgress}%` }}></div>
          </div>
          <p>Converting to MP4... {conversionProgress}%</p>
        </div>
      )}
    </div>
  );
}

export default LiveRecorder;
